import {
    collection,
    doc,
    setDoc,
    getDoc,
    updateDoc,
    getDocs,
    query,
    where,
    serverTimestamp,
    arrayUnion,
    runTransaction,
    getCountFromServer,
    CollectionReference,
    DocumentData
} from 'firebase/firestore';
import { db } from '@lib/firebase';

import { ValidationError } from '@/shared/api/errors/ApiError';
import ServiceBase from '@/shared/api/base/ServiceBase';
import { loggingService } from '@/shared/services';

const MAX_DAILY_HOURS = 4 * 3600;
const MIN_BREAK_DURATION = 10 * 60;
const BREAK_REQUIRED_AFTER = 2 * 3600;

// Extend window for E2E test mocking
declare global {
    interface Window {
        MOCK_SESSION_ID?: string;
        MOCK_DAILY_TIME?: number;
    }
}

// Type definitions
interface SessionData {
    ipAddress?: string | null;
    deviceInfo?: string | null;
    userAgent?: string | null;
    startTime?: string | any;
}

interface ComplianceSession {
    id?: string;
    sessionId?: string;
    userId: string;
    courseId: string;
    startTime?: any;
    startTimestamp?: number;
    lastHeartbeatTimestamp?: number;
    ipAddress?: string | null;
    deviceInfo?: string | null;
    userAgent?: string | null;
    status: string;
    lastHeartbeat?: any;
    completionEvents?: CompletionEvent[];
    breaks?: Break[];
    createdAt?: any;
    endTime?: any;
    endTimestamp?: number;
    duration?: number;
    videoProgress?: any;
    lessonsAccessed?: string[];
    closureType?: string;
    closedAt?: any;
    updatedAt?: any;
}

interface Break {
    startTime?: any;
    endTime?: any;
    reason?: string;
    status?: string;
    initiatedAt?: string;
    actualDuration?: number;
    validatedByServer?: boolean;
    validationTimestamp?: string;
}

interface CompletionEvent {
    type: string;
    quizId?: string;
    quizTitle?: string;
    score?: number;
    passingScore?: number;
    passed?: boolean;
    attemptNumber?: number;
    completedAt?: any;
    timestamp?: any;
}

interface BreakData {
    reason?: string;
}

interface CloseSessionData {
    duration?: number;
    videoProgress?: any;
    lessonsAccessed?: string[];
    breaks?: any[];
    closureType?: string;
    // No change needed here, just confirming I checked it.
    lastUpdated?: string | any;
}

interface PVQData {
    pvqId: string;
    questionsAnswered?: number;
    correctAnswers?: number;
    passed?: boolean;
}

interface QuizAttemptData {
    quizId: string;
    quizTitle?: string;
    score?: number;
    passingScore?: number;
    passed?: boolean;
    attemptNumber?: number;
}

interface BreakEndResult {
    actualDurationSeconds: number;
    validated: boolean;
}

interface BreakTimeRemaining {
    remainingSeconds: number;
    elapsedSeconds: number;
    breakStartTime: number;
    currentTime: number;
    isBreakComplete: boolean;
}

interface OrphanedSessionsResult {
    closedCount: number;
}

interface InactivityTimeoutResult {
    success: boolean;
    [key: string]: any;
}

class ComplianceService extends ServiceBase {
    constructor() {
        super('ComplianceService');
    }

    // Centralized Audit Logger for OAC Compliance
    async auditLog(userId: string, action: string, metadata: Record<string, any> = {}): Promise<void> {
        try {
            await loggingService.logAuditEvent(
                userId,
                action,
                'compliance',
                metadata.sessionId || 'N/A',
                metadata.status || 'success',
                {
                    ...metadata,
                    ipAddress: metadata.ipAddress || 'client-side'
                }
            );
        } catch (error) {
            console.error('Failed to write audit log:', error);
        }
    }

    // Helper function to get sessions subcollection reference
    _getSessionsRef(userId: string): CollectionReference<DocumentData> {
        return collection(db, 'users', userId, 'sessions');
    }

    // Create a new compliance session (Sequential IDs: 1, 2, 3...)
    async createComplianceSession(userId: string, courseId: string, data: SessionData): Promise<string> {
        try {
            this.validate.validateUserId(userId);
            this.validate.validateCourseId(courseId);

            // E2E Test Mock Injection
            if (typeof window !== 'undefined' && window.MOCK_SESSION_ID) {
                console.warn('Returning MOCKED Session ID for E2E testing');
                return window.MOCK_SESSION_ID;
            }

            if (!data || typeof data !== 'object') {
                throw new ValidationError('data must be a non-empty object');
            }

            // Centralized Compliance Guard: Verify Daily Hours before creating session
            const isLocked = await this.checkDailyHourLockout(userId, courseId);
            if (isLocked) {
                const error = new Error('DAILY_LIMIT_REACHED: You have reached the 4-hour daily training limit. New sessions are blocked until midnight.') as any;
                error.code = 'functions/resource-exhausted'; // Align with Cloud Function error codes
                throw error;
            }

            // 1. Find ANY active sessions to terminate (Concurrent Enforcement)
            const sessionsRef = this._getSessionsRef(userId);
            const activeQuery = query(sessionsRef, where('status', '==', 'active'));
            const activeSnapshot = await getDocs(activeQuery);

            // 2. Determine Next Session Number
            const counterRef = doc(db, 'users', userId, 'counters', 'session_stats');
            const counterDoc = await getDoc(counterRef);

            let nextSessionNumber: number;
            if (counterDoc.exists()) {
                nextSessionNumber = (counterDoc.data().totalSessions || 0) + 1;
            } else {
                // Fallback: Count total documents to recover sequential ordering
                const countSnapshot = await getCountFromServer(sessionsRef);
                nextSessionNumber = countSnapshot.data().count + 1;
            }

            return await runTransaction(db, async (transaction) => {
                // Re-fetch counter inside transaction for ACID safety
                const tCounterDoc = await transaction.get(counterRef);
                let finalSessionNumber = nextSessionNumber;

                if (tCounterDoc.exists()) {
                    finalSessionNumber = (tCounterDoc.data().totalSessions || 0) + 1;
                }

                const newSessionId = String(finalSessionNumber);
                const sessionRef = doc(db, 'users', userId, 'sessions', newSessionId);

                // 3. Prepare Session Data
                const nowMs = new Date().getTime();
                const sessionData = {
                    userId,
                    courseId,
                    sessionId: newSessionId,
                    startTime: serverTimestamp(),
                    startTimestamp: nowMs,
                    lastHeartbeatTimestamp: nowMs,
                    ipAddress: data.ipAddress || null,
                    deviceInfo: data.deviceInfo || null,
                    userAgent: data.userAgent || null,
                    status: 'active',
                    lastHeartbeat: serverTimestamp(),
                    completionEvents: [],
                    breaks: [],
                    createdAt: serverTimestamp()
                };

                // 4. Terminate any previous active sessions discovered
                activeSnapshot.forEach((docSnap) => {
                    const oldSessionId = docSnap.id;
                    transaction.update(docSnap.ref, {
                        status: 'terminated_concurrent',
                        endTime: serverTimestamp(),
                        endTimestamp: nowMs,
                        closureType: 'concurrent_new_session',
                        closedAt: serverTimestamp(),
                        updatedAt: serverTimestamp(),
                        auditFlag: 'CONCURRENT_TERMINATION_AUTO'
                    });

                    // Log audit event asynchronously after transaction
                    this.auditLog(userId, 'CONCURRENT_SESSION_TERMINATED', {
                        sessionId: oldSessionId,
                        newSessionId: newSessionId,
                        reason: 'New session started on different device/tab'
                    });
                });

                // 5. Commit Writes
                transaction.set(sessionRef, sessionData);
                transaction.set(counterRef, { totalSessions: finalSessionNumber }, { merge: true });

                return newSessionId;
            });
        } catch (error) {
            this.logError(error, { method: 'createComplianceSession', userId, courseId });
            throw error;
        }
    }

    // Check for any currently active sessions for this user
    async getActiveSession(userId: string): Promise<ComplianceSession | null> {
        try {
            this.validate.validateUserId(userId);
            const sessionsRef = this._getSessionsRef(userId);
            const q = query(
                sessionsRef,
                where('status', '==', 'active')
            );
            const querySnapshot = await getDocs(q);

            if (querySnapshot.empty) return null;

            // Check heartbeat to see if it's actually active
            const now = Date.now();
            const activeSessions = querySnapshot.docs.map(docSnap => ({
                id: docSnap.id,
                ...docSnap.data()
            } as ComplianceSession)).filter(session => {
                // If there is an active break, the session is active regardless of heartbeat
                const breaks = session.breaks || [];
                const hasActiveBreak = breaks.length > 0 && breaks[breaks.length - 1].status === 'active';
                if (hasActiveBreak) return true;

                // If heartbeat is within last 2 minutes, consider it active
                const lastHeartbeat = session.lastHeartbeatTimestamp || 0;
                return (now - lastHeartbeat) < (2 * 60 * 1000);
            });

            return activeSessions.length > 0 ? activeSessions[0] : null;
        } catch (error) {
            this.logError(error, { method: 'getActiveSession', userId });
            throw error;
        }
    }

    // Update compliance session (PHASE 1 - Issue #2: Heartbeat)
    async updateComplianceSession(userId: string, sessionId: string, updates: Partial<DocumentData>): Promise<void> {
        try {
            // E2E Test Mock Injection
            if (typeof window !== 'undefined' && window.MOCK_SESSION_ID) {
                console.warn('Returning MOCKED Update Compliance Session for E2E testing');
                return;
            }

            if (!sessionId || typeof sessionId !== 'string') {
                throw new ValidationError('sessionId must be a non-empty string');
            }
            if (!updates || typeof updates !== 'object') {
                throw new ValidationError('updates must be a non-empty object');
            }

            const sessionsRef = this._getSessionsRef(userId);
            const sessionRef = doc(sessionsRef, sessionId);

            await updateDoc(sessionRef, {
                ...updates,
                lastUpdated: serverTimestamp()
            });
        } catch (error) {
            this.logError(error, { method: 'updateComplianceSession', userId, sessionId });
            throw error;
        }
    }

    // Close a compliance session
    async closeComplianceSession(userId: string, sessionId: string, sessionData: CloseSessionData): Promise<void> {
        try {
            // E2E Test Mock Injection
            if (typeof window !== 'undefined' && window.MOCK_SESSION_ID) {
                console.warn('Returning MOCKED Close Compliance Session for E2E testing');
                return;
            }

            if (!sessionId || typeof sessionId !== 'string') {
                throw new ValidationError('sessionId must be a non-empty string');
            }
            if (!sessionData || typeof sessionData !== 'object') {
                throw new ValidationError('sessionData must be a non-empty object');
            }

            const sessionsRef = this._getSessionsRef(userId);
            const sessionRef = doc(sessionsRef, sessionId);

            const updateData: any = {
                endTime: serverTimestamp(),
                endTimestamp: new Date().getTime(),
                status: sessionData.closureType === 'concurrent_termination' ? 'terminated_concurrent' :
                    sessionData.closureType === 'page_unload' ? 'unloaded' : 'completed',
                closureType: sessionData.closureType || 'normal_exit',
                closedAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            };

            if (sessionData.duration !== undefined) updateData.duration = sessionData.duration;
            if (sessionData.videoProgress !== undefined) updateData.videoProgress = sessionData.videoProgress;
            if (sessionData.lessonsAccessed !== undefined) updateData.lessonsAccessed = sessionData.lessonsAccessed;
            if (sessionData.breaks !== undefined) updateData.breaks = sessionData.breaks;

            await updateDoc(sessionRef, updateData);
            this.log(`Closed compliance session ${sessionId} for user ${userId}`);
        } catch (error) {
            this.logError(error, { method: 'closeComplianceSession', userId, sessionId });
            throw error;
        }
    }

    // Get daily time spent on course
    async getDailyTime(userId: string, courseId: string): Promise<number> {
        try {
            this.validate.validateUserId(userId);
            this.validate.validateCourseId(courseId);

            // E2E Test Mock Injection
            if (typeof window !== 'undefined' && typeof window.MOCK_DAILY_TIME !== 'undefined') {
                console.warn('Returning MOCKED Daily Time for E2E testing');
                return window.MOCK_DAILY_TIME;
            }

            // COMPLIANCE: Midnight reset must match the School's local timezone (EST/EDT)
            // matching complianceFunctions.ts server-side enforcement.
            const TIMEZONE = 'America/New_York';
            const now = new Date();
            const estDateString = now.toLocaleString('en-US', { timeZone: TIMEZONE });
            const estNow = new Date(estDateString);

            const estMidnight = new Date(estDateString);
            estMidnight.setHours(0, 0, 0, 0);

            // Calculate the UTC timestamp for EST midnight to compare with Firestore Timestamps
            // We need to know when midnight EST occurred in absolute UTC time.
            const midnightTimestamp = estMidnight.getTime();

            const sessionsRef = this._getSessionsRef(userId);
            const q = query(
                sessionsRef,
                where('courseId', '==', courseId)
            );

            const querySnapshot = await getDocs(q);
            let totalTime = 0;

            querySnapshot.forEach((docSnap) => {
                const session = docSnap.data() as ComplianceSession;
                // Count all closed session types AND active sessions (for accurate Today's Limit)
                const isCountable = ['completed', 'unloaded', 'force_close', 'component_unmount', 'active', 'terminated_concurrent', 'timeout', 'idle_timeout'].includes(session.status);

                if (isCountable && session.startTime) {
                    // Handle Firestore Timestamp vs Date string
                    let startTime: Date;
                    if ((session.startTime as { toDate?: () => Date })?.toDate) {
                        startTime = (session.startTime as { toDate: () => Date }).toDate();
                    } else if (session.startTime instanceof Date) {
                        startTime = session.startTime;
                    } else {
                        startTime = new Date(session.startTime);
                    }

                    if (!isNaN(startTime.getTime()) && startTime.getTime() >= midnightTimestamp) {
                        totalTime += session.duration || 0;
                    }
                }
            });

            return totalTime;
        } catch (error) {
            this.logError(error, { method: 'getDailyTime', userId, courseId });
            throw error;
        }
    }

    // Get continuous daily instruction time since the latest completed mandatory break today
    async getDailyTimeSinceLastBreak(userId: string, courseId: string, excludeSessionId?: string): Promise<number> {
        try {
            this.validate.validateUserId(userId);
            this.validate.validateCourseId(courseId);

            // COMPLIANCE: Midnight reset matches Eastern Time (EST/EDT)
            const TIMEZONE = 'America/New_York';
            const now = new Date();
            const estDateString = now.toLocaleString('en-US', { timeZone: TIMEZONE });
            const estMidnight = new Date(estDateString);
            estMidnight.setHours(0, 0, 0, 0);
            const midnightTimestamp = estMidnight.getTime();

            const sessionsRef = this._getSessionsRef(userId);
            const q = query(
                sessionsRef,
                where('courseId', '==', courseId)
            );

            const querySnapshot = await getDocs(q);
            const sessionsToday: (ComplianceSession & { startTimestamp: number })[] = [];

            querySnapshot.forEach((docSnap) => {
                const session = docSnap.data() as ComplianceSession;
                
                // Exclude the currently active/resumed session from double counting
                if (excludeSessionId && docSnap.id === excludeSessionId) {
                    return;
                }

                const isCountable = ['completed', 'unloaded', 'force_close', 'component_unmount', 'active', 'terminated_concurrent', 'timeout', 'idle_timeout'].includes(session.status);

                if (isCountable && session.startTime) {
                    let startTime: Date;
                    if ((session.startTime as { toDate?: () => Date })?.toDate) {
                        startTime = (session.startTime as { toDate: () => Date }).toDate();
                    } else if (session.startTime instanceof Date) {
                        startTime = session.startTime;
                    } else {
                        startTime = new Date(session.startTime);
                    }

                    if (!isNaN(startTime.getTime()) && startTime.getTime() >= midnightTimestamp) {
                        sessionsToday.push({
                            ...session,
                            id: docSnap.id,
                            startTimestamp: startTime.getTime()
                        });
                    }
                }
            });

            // Sort chronologically by start time (ascending)
            sessionsToday.sort((a, b) => a.startTimestamp - b.startTimestamp);

            let instructionSinceLastBreak = 0;

            for (const session of sessionsToday) {
                const breaks = session.breaks || [];
                const completedBreaks = breaks.filter(
                    b => b.reason === 'mandatory' && (b.status === 'completed' || !!b.endTime)
                );

                if (completedBreaks.length > 0) {
                    // Reset cumulative tally, only count instruction accumulated AFTER the last completed break
                    const sessionInstructionAfterBreak = Math.max(
                        0,
                        (session.duration || 0) - completedBreaks.length * BREAK_REQUIRED_AFTER
                    );
                    instructionSinceLastBreak = sessionInstructionAfterBreak;
                } else {
                    // Accumulate entire session duration
                    instructionSinceLastBreak += session.duration || 0;
                }
            }

            return instructionSinceLastBreak;
        } catch (error) {
            this.logError(error, { method: 'getDailyTimeSinceLastBreak', userId, excludeSessionId });
            throw error;
        }
    }

    // Check if user has hit daily hour lockout
    async checkDailyHourLockout(userId: string, courseId: string): Promise<boolean> {
        try {
            this.validate.validateUserId(userId);
            this.validate.validateCourseId(courseId);

            const dailyTime = await this.getDailyTime(userId, courseId);
            return dailyTime >= MAX_DAILY_HOURS;
        } catch (error) {
            this.logError(error, { method: 'checkDailyHourLockout', userId, courseId });
            throw error;
        }
    }

    // Get session history for a user in a course
    async getSessionHistory(userId: string, courseId: string, limitCount: number = 50): Promise<ComplianceSession[]> {
        try {
            this.validate.validateUserId(userId);
            this.validate.validateCourseId(courseId);

            const sessionsRef = this._getSessionsRef(userId);
            const q = query(
                sessionsRef,
                where('courseId', '==', courseId)
            );

            const querySnapshot = await getDocs(q);
            const sessions: ComplianceSession[] = [];

            querySnapshot.forEach((docSnap) => {
                sessions.push({
                    sessionId: docSnap.id,
                    ...docSnap.data()
                } as ComplianceSession);
            });

            return sessions.slice(0, limitCount);
        } catch (error) {
            this.logError(error, { method: 'getSessionHistory', userId, courseId });
            throw error;
        }
    }

    // Log start of break (SECURITY: Server timestamps only, no client duration)
    async logBreak(userId: string, sessionId: string, breakData: BreakData): Promise<void> {
        try {
            // E2E Test Mock Injection
            if (typeof window !== 'undefined' && window.MOCK_SESSION_ID) {
                console.warn('Returning MOCKED Log Break for E2E testing');
                return;
            }

            if (!sessionId || typeof sessionId !== 'string') {
                throw new ValidationError('sessionId must be a non-empty string');
            }
            if (!breakData || typeof breakData !== 'object') {
                throw new ValidationError('breakData must be a non-empty object');
            }

            // SECURITY: Validate break reason
            const validReasons = ['mandatory', 'user_initiated'];
            const breakReason = breakData.reason || 'user_initiated';
            if (!validReasons.includes(breakReason)) {
                throw new ValidationError(`Invalid break reason: ${breakReason}`);
            }

            const sessionsRef = this._getSessionsRef(userId);
            const sessionRef = doc(sessionsRef, sessionId);
            const now = new Date().toISOString();

            await updateDoc(sessionRef, {
                breaks: arrayUnion({
                    startTime: now,
                    // SECURITY: Do NOT store client-supplied duration. Duration calculated server-side at break end.
                    reason: breakReason,
                    status: 'active',  // Will be 'completed' after logBreakEnd() validates
                    initiatedAt: now
                }),
                lastHeartbeatTimestamp: new Date().getTime(),
                lastHeartbeat: serverTimestamp()
            });
            this.log(`Logged break for user ${userId}, session ${sessionId}`);
        } catch (error) {
            this.logError(error, { method: 'logBreak', userId, sessionId });
            throw error;
        }
    }

    // Log end of break - Server-side duration calculation (SECURITY: Not trusting client)
    async logBreakEnd(userId: string, sessionId: string): Promise<BreakEndResult> {
        try {
            // E2E Test Mock Injection
            if (typeof window !== 'undefined') {
                console.log('[complianceServices] checking mocks', {
                    error: (window as any).MOCK_COMPLIANCE_ERROR,
                    session: (window as any).MOCK_SESSION_ID
                });
                if ((window as any).MOCK_COMPLIANCE_ERROR) {
                    console.warn('[complianceServices] THROWING MOCKED ERROR:', (window as any).MOCK_COMPLIANCE_ERROR);
                    throw (window as any).MOCK_COMPLIANCE_ERROR;
                }
                if ((window as any).MOCK_SESSION_ID) {
                    console.warn('[complianceServices] Returning MOCKED successful result');
                    return {
                        actualDurationSeconds: 600,
                        validated: true
                    };
                }
            }

            if (!sessionId || typeof sessionId !== 'string') {
                throw new ValidationError('sessionId must be a non-empty string');
            }

            const sessionsRef = this._getSessionsRef(userId);
            const sessionRef = doc(sessionsRef, sessionId);
            const sessionDoc = await getDoc(sessionRef);

            if (!sessionDoc.exists()) {
                throw new ValidationError('Session not found');
            }

            const breaks = (sessionDoc.data().breaks || []) as Break[];
            if (breaks.length === 0) {
                throw new ValidationError('No active break found');
            }

            const lastBreak = breaks[breaks.length - 1];

            // SECURITY: Server calculates duration from server timestamps (not client claim)
            let breakStartMs: number;
            if ((lastBreak.startTime as { toMillis?: () => number })?.toMillis?.()) {
                // Firestore Timestamp object
                breakStartMs = (lastBreak.startTime as { toMillis: () => number }).toMillis();
            } else if (typeof lastBreak.startTime === 'string') {
                // ISO string
                breakStartMs = new Date(lastBreak.startTime).getTime();
            } else if (lastBreak.startTime instanceof Date) {
                // JavaScript Date
                breakStartMs = lastBreak.startTime.getTime();
            } else {
                // Numeric timestamp
                breakStartMs = lastBreak.startTime as number;
            }

            if (!breakStartMs) {
                throw new ValidationError('Break start time is invalid');
            }

            const breakEndMs = Date.now();  // Server's current time (not client time!)
            const breakDurationSeconds = Math.floor((breakEndMs - breakStartMs) / 1000);

            // SECURITY: Server enforces minimum break duration with a sub-second tolerance buffer
            if (breakDurationSeconds < (MIN_BREAK_DURATION - 5)) {
                const minutesRemaining = Math.ceil((MIN_BREAK_DURATION - breakDurationSeconds) / 60);
                const error = new ValidationError(
                    `Break must be at least ${MIN_BREAK_DURATION / 60} minutes. ` +
                    `Current: ${Math.floor(breakDurationSeconds / 60)} minutes. ` +
                    `${minutesRemaining} minute(s) remaining.`
                ) as ValidationError & { code?: string; minutesRemaining?: number; currentDurationSeconds?: number };
                error.code = 'BREAK_TOO_SHORT';
                error.minutesRemaining = minutesRemaining;
                error.currentDurationSeconds = breakDurationSeconds;
                throw error;
            }

            // SECURITY: Update with server-calculated values
            const now = new Date().toISOString();
            lastBreak.endTime = now;
            lastBreak.actualDuration = breakDurationSeconds;  // Server calculated, not client supplied
            lastBreak.validatedByServer = true;               // Audit flag
            lastBreak.validationTimestamp = now;

            await updateDoc(sessionRef, {
                breaks
            });

            this.log(`Logged break end for user ${userId}, session ${sessionId}, duration ${breakDurationSeconds}s`);

            // Return actual duration for audit purposes
            return {
                actualDurationSeconds: breakDurationSeconds,
                validated: true
            };
        } catch (error) {
            this.logError(error, { method: 'logBreakEnd', userId, sessionId });
            throw error;
        }
    }

    // Log identity verification (PVQ)
    async logIdentityVerification(userId: string, courseId: string, pvqData: PVQData): Promise<string> {
        try {
            // E2E Test Mock Injection
            if (typeof window !== 'undefined' && window.MOCK_SESSION_ID) {
                console.warn('Returning MOCKED Log Identity Verification for E2E testing');
                return 'mock-verification-id';
            }

            this.validate.validateUserId(userId);
            this.validate.validateCourseId(courseId);
            if (!pvqData || typeof pvqData !== 'object') {
                throw new ValidationError('pvqData must be a non-empty object');
            }

            const verificationRef = doc(collection(db, 'users', userId, 'identityVerifications'));

            await setDoc(verificationRef, {
                userId,
                courseId,
                pvqId: pvqData.pvqId,
                questionsAnswered: pvqData.questionsAnswered || 0,
                correctAnswers: pvqData.correctAnswers || 0,
                passed: pvqData.passed || false,
                verifiedAt: serverTimestamp(),
                createdAt: serverTimestamp()
            });

            this.log(`Logged identity verification for user ${userId}, course ${courseId}`);
            return verificationRef.id;
        } catch (error) {
            this.logError(error, { method: 'logIdentityVerification', userId, courseId });
            throw error;
        }
    }

    // Log quiz attempt
    async logQuizAttempt(userId: string, sessionId: string, quizAttemptData: QuizAttemptData): Promise<void> {
        try {
            // E2E Test Mock Injection
            if (typeof window !== 'undefined' && window.MOCK_SESSION_ID) {
                console.warn('Returning MOCKED Log Quiz Attempt for E2E testing');
                return;
            }

            if (!sessionId || typeof sessionId !== 'string') {
                throw new ValidationError('sessionId must be a non-empty string');
            }
            if (!quizAttemptData || typeof quizAttemptData !== 'object') {
                throw new ValidationError('quizAttemptData must be a non-empty object');
            }

            const sessionsRef = this._getSessionsRef(userId);
            const sessionRef = doc(sessionsRef, sessionId);

            await updateDoc(sessionRef, {
                completionEvents: arrayUnion({
                    type: 'quiz_attempt',
                    quizId: quizAttemptData.quizId,
                    quizTitle: quizAttemptData.quizTitle,
                    score: quizAttemptData.score || 0,
                    passingScore: quizAttemptData.passingScore || 0,
                    passed: quizAttemptData.passed || false,
                    attemptNumber: quizAttemptData.attemptNumber || 1,
                    completedAt: serverTimestamp(),
                    timestamp: serverTimestamp()
                })
            });
            this.log(`Logged quiz attempt for user ${userId}, session ${sessionId}, quiz ${quizAttemptData.quizId}`);
        } catch (error) {
            this.logError(error, { method: 'logQuizAttempt', userId, sessionId });
            throw error;
        }
    }

    // Get total session time for user in course
    async getTotalSessionTime(userId: string, courseId: string): Promise<number> {
        try {
            //Note: getTotalSessionTime gets ALL sessions, not just today
            this.validate.validateUserId(userId);
            this.validate.validateCourseId(courseId);

            const sessionsRef = this._getSessionsRef(userId);
            const q = query(
                sessionsRef,
                where('courseId', '==', courseId),
                where('status', '==', 'completed')
            );

            const querySnapshot = await getDocs(q);
            let totalTime = 0;

            querySnapshot.forEach((docSnap) => {
                const session = docSnap.data() as ComplianceSession;
                if (session.status === 'completed') {
                    totalTime += session.duration || 0;
                }
            });

            return totalTime;
        } catch (error) {
            this.logError(error, { method: 'getTotalSessionTime', userId, courseId });
            throw error;
        }
    }

    // Get total session time in minutes
    async getTotalSessionTimeInMinutes(userId: string, courseId: string): Promise<number> {
        try {
            const totalSeconds = await this.getTotalSessionTime(userId, courseId);
            return Math.round(totalSeconds / 60);
        } catch (error) {
            this.logError(error, { method: 'getTotalSessionTimeInMinutes', userId, courseId });
            throw error;
        }
    }

    // PHASE 1 - Issue #2: Detect and close orphaned sessions
    async handleOrphanedSessions(userId: string, courseId: string): Promise<OrphanedSessionsResult> {
        try {
            this.validate.validateUserId(userId);
            this.validate.validateCourseId(courseId);

            const now = new Date().getTime();
            const thirtyMinutesAgo = now - (30 * 60 * 1000);

            const sessionsRef = this._getSessionsRef(userId);
            const q = query(
                sessionsRef,
                where('courseId', '==', courseId),
                where('status', '==', 'active')
            );

            const orphanedSnapshots = await getDocs(q);
            const closedSessions: { sessionId: string; duration: number }[] = [];

            for (const sessionDoc of orphanedSnapshots.docs) {
                const session = sessionDoc.data() as ComplianceSession;
                const lastHeartbeatTime = (session.lastHeartbeat as { toMillis?: () => number })?.toMillis?.() || session.startTimestamp || 0;

                if (lastHeartbeatTime < thirtyMinutesAgo) {
                    try {
                        const startTime = session.startTimestamp || 0;
                        const duration = Math.floor((now - startTime) / 1000);

                        await updateDoc(sessionDoc.ref, {
                            status: 'timeout',
                            endTime: serverTimestamp(),
                            endTimestamp: now,
                            duration: duration,
                            closureType: 'orphaned_auto_close',
                            auditFlag: 'SESSION_ABANDONED_30MIN',
                            updatedAt: serverTimestamp()
                        });

                        closedSessions.push({
                            sessionId: sessionDoc.id,
                            duration: duration
                        });
                    } catch (error) {
                        console.error(`Failed to close orphaned session ${sessionDoc.id}:`, error);
                    }
                }
            }

            if (closedSessions.length > 0) {
                this.log(`Handled ${closedSessions.length} orphaned sessions for user ${userId}`);
            }
            return { closedCount: closedSessions.length };
        } catch (error) {
            this.logError(error, { method: 'handleOrphanedSessions', userId, courseId });
            throw error;
        }
    }

    async enforceInactivityTimeout(
        userId: string,
        courseId: string,
        sessionId: string,
        idleDurationSeconds: number
    ): Promise<InactivityTimeoutResult> {
        try {
            // E2E Test Mock Injection
            if (typeof window !== 'undefined' && window.MOCK_SESSION_ID) {
                console.warn('Returning MOCKED Enforce Inactivity Timeout for E2E testing');
                return { success: true };
            }

            this.validate.validateUserId(userId);
            this.validate.validateCourseId(courseId);

            if (!sessionId) {
                throw new ValidationError('sessionId is required');
            }

            if (typeof idleDurationSeconds !== 'number' || idleDurationSeconds < 0) {
                throw new ValidationError('idleDurationSeconds must be a non-negative number');
            }

            const { httpsCallable, getFunctions } = await import('firebase/functions');
            const functions = getFunctions();
            const enforceInactivityTimeoutFn = httpsCallable(functions, 'enforceInactivityTimeout');

            const result = await enforceInactivityTimeoutFn({
                userId,
                courseId,
                sessionId,
                idleDurationSeconds
            });

            this.log(`Enforced inactivity timeout for user ${userId}, session ${sessionId}`);
            return result.data as InactivityTimeoutResult;
        } catch (error) {
            this.logError(error, { method: 'enforceInactivityTimeout', userId, sessionId });
            throw error;
        }
    }

    // Get server-calculated remaining break time
    async getBreakTimeRemaining(userId: string, sessionId: string): Promise<BreakTimeRemaining> {
        try {
            // E2E Test Mock Injection
            if (typeof window !== 'undefined' && window.MOCK_SESSION_ID) {
                console.warn('Returning MOCKED Break Time Remaining for E2E testing');
                return {
                    remainingSeconds: 600,
                    elapsedSeconds: 0,
                    breakStartTime: Date.now(),
                    currentTime: Date.now(),
                    isBreakComplete: false
                };
            }

            if (!sessionId || typeof sessionId !== 'string') {
                throw new ValidationError('sessionId must be a non-empty string');
            }

            const sessionsRef = this._getSessionsRef(userId);
            const sessionRef = doc(sessionsRef, sessionId);
            const sessionDoc = await getDoc(sessionRef);

            if (!sessionDoc.exists()) {
                throw new ValidationError('Session not found');
            }

            const breaks = (sessionDoc.data().breaks || []) as Break[];
            if (breaks.length === 0) {
                throw new ValidationError('No active break found');
            }

            const lastBreak = breaks[breaks.length - 1];

            // Parse break start time (supports multiple formats)
            let breakStartMs: number;
            if ((lastBreak.startTime as { toMillis?: () => number })?.toMillis?.()) {
                breakStartMs = (lastBreak.startTime as { toMillis: () => number }).toMillis();
            } else if (typeof lastBreak.startTime === 'string') {
                breakStartMs = new Date(lastBreak.startTime).getTime();
            } else if (lastBreak.startTime instanceof Date) {
                breakStartMs = lastBreak.startTime.getTime();
            } else {
                breakStartMs = lastBreak.startTime as number;
            }

            if (!breakStartMs) {
                throw new ValidationError('Break start time is invalid');
            }

            const now = Date.now();
            const elapsedSeconds = Math.floor((now - breakStartMs) / 1000);
            const remainingSeconds = Math.max(0, MIN_BREAK_DURATION - elapsedSeconds);

            return {
                remainingSeconds,
                elapsedSeconds,
                breakStartTime: breakStartMs,
                currentTime: now,
                isBreakComplete: remainingSeconds === 0
            };
        } catch (error) {
            this.logError(error, { method: 'getBreakTimeRemaining', userId, sessionId });
            throw error;
        }
    }
}

const complianceServices = new ComplianceService();
export default complianceServices;

// Named exports for backward compatibility
export const createComplianceSession = (userId: string, courseId: string, data: SessionData) => complianceServices.createComplianceSession(userId, courseId, data);
export const getActiveSession = (userId: string) => complianceServices.getActiveSession(userId);
export const updateComplianceSession = (userId: string, sessionId: string, updates: Partial<DocumentData>) => complianceServices.updateComplianceSession(userId, sessionId, updates);
export const closeComplianceSession = (userId: string, sessionId: string, sessionData: CloseSessionData) => complianceServices.closeComplianceSession(userId, sessionId, sessionData);
export const getDailyTime = (userId: string, courseId: string) => complianceServices.getDailyTime(userId, courseId);
export const checkDailyHourLockout = (userId: string, courseId: string) => complianceServices.checkDailyHourLockout(userId, courseId);
export const getSessionHistory = (userId: string, courseId: string, limitCount?: number) => complianceServices.getSessionHistory(userId, courseId, limitCount);
export const logBreak = (userId: string, sessionId: string, breakData: BreakData) => complianceServices.logBreak(userId, sessionId, breakData);
export const logBreakEnd = (userId: string, sessionId: string) => complianceServices.logBreakEnd(userId, sessionId);
export const logIdentityVerification = (userId: string, courseId: string, pvqData: PVQData) => complianceServices.logIdentityVerification(userId, courseId, pvqData);
export const logQuizAttempt = (userId: string, sessionId: string, quizAttemptData: QuizAttemptData) => complianceServices.logQuizAttempt(userId, sessionId, quizAttemptData);
export const getTotalSessionTime = (userId: string, courseId: string) => complianceServices.getTotalSessionTime(userId, courseId);
export const getTotalSessionTimeInMinutes = (userId: string, courseId: string) => complianceServices.getTotalSessionTimeInMinutes(userId, courseId);
export const handleOrphanedSessions = (userId: string, courseId: string) => complianceServices.handleOrphanedSessions(userId, courseId);
export const enforceInactivityTimeout = (userId: string, courseId: string, sessionId: string, idleDurationSeconds: number) => complianceServices.enforceInactivityTimeout(userId, courseId, sessionId, idleDurationSeconds);
export const getBreakTimeRemaining = (userId: string, sessionId: string) => complianceServices.getBreakTimeRemaining(userId, sessionId);
export const getDailyTimeSinceLastBreak = (userId: string, courseId: string, excludeSessionId?: string) => complianceServices.getDailyTimeSinceLastBreak(userId, courseId, excludeSessionId);

